import { find, isArray, isPlainObject, sumBy, uniqBy } from 'lodash';
import { format as dateFormat } from 'date-fns';
import currency from 'currency.js';
import parse from 'html-react-parser';
import { FORMAT_QT_DATE } from 'utils/helpers/datetime';

// Converts deeply nested params hash to an array of keys and values suitable for appending onto a URL
const convertToSearchParam = (obj, prefixKey = '') => {
  let keyValuePairs = [];
  Object.keys(obj).forEach((hashKey) => {
    const key = prefixKey.length > 0 ? `${prefixKey}[${hashKey}]` : hashKey;
    const value = obj[hashKey];
    if (isPlainObject(value)) {
      keyValuePairs = [...keyValuePairs, ...convertToSearchParam(value, key)];
    } else if (isArray(value)) {
      value.forEach((item) => {
        if (isPlainObject(item)) {
          keyValuePairs = [
            ...keyValuePairs,
            ...convertToSearchParam(item, `${key}[]`),
          ];
        } else {
          keyValuePairs.push([`${key}[]`, item]);
        }
      });
    } else {
      keyValuePairs.push([key, value]);
    }
  });

  return keyValuePairs;
};

export function getParamFromUrl() {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);

  return Object.fromEntries(urlParams);
}

export function generateURL(path, params, isEcomApi = true) {
  const qtApiPrefix = window.ecom?.craftConfig?.api_prefix || '';
  const baseUrl = isEcomApi ? `${qtApiPrefix}/ecom/api/v1` : '';
  let finalURL = `${window.location.origin}${baseUrl}${path}`;

  if (params) {
    const searchParams = new URLSearchParams();

    const paramsArray = convertToSearchParam(params);
    paramsArray.forEach((param) => {
      searchParams.append(...param);
    });

    finalURL = `${finalURL}?${searchParams}`;
  }

  return finalURL;
}

export function price(
  value,
  configs = { fromCents: true, omitZeroCents: false }
) {
  return value === undefined || Number.isNaN(value)
    ? ''
    : currency(value, configs).format(
        // https://github.com/scurker/currency.js/issues/191
        (instance, options) => {
          // Call original format function to get its string value
          const format = options.format(instance, options);

          if (instance.cents() === 0 && configs.omitZeroCents) {
            // Trim decimal and any trailing zeros
            return format.slice(0, format.length - (options.precision + 1));
          }

          return format;
        }
      );
}

// Retured data looks like this: `{ Adult: { price: 16500, count: 1 }, Child: { price: 8900, count: 1 } }`.
export function processPassengerType(
  passengerSplits,
  passengers,
  passengerTypes,
  subReservationsAttributes = null
) {
  const processedPassengerType = {};

  passengerSplits.forEach((item) => {
    const passenger = find(passengers, { id: item.consumer_id });
    const passengerType = find(passengerTypes, {
      id: passenger.passenger_type_id,
    });
    const { name } = passengerType;

    processedPassengerType[name] = processedPassengerType[name] || {
      price: item.gross_in_cents,
      consumer_id: item.consumer_id,
    };
    const existingCount = processedPassengerType[name].count;
    const count = existingCount + 1 || 1;

    processedPassengerType[name].count = count;
  });

  if (subReservationsAttributes && subReservationsAttributes.length > 0) {
    subReservationsAttributes.forEach((subReservation) => {
      const processedSubReservationPassengerType = processPassengerType(
        subReservation.passenger_splits,
        passengers,
        passengerTypes,
        subReservation.sub_reservations_attributes
      );

      Object.keys(processedSubReservationPassengerType).forEach((key) => {
        processedPassengerType[key].price +=
          processedSubReservationPassengerType[key].price;
      });
    });
  }

  return processedPassengerType;
}

// Calculate original price from a reservations attributes list
export function getOriginalPrice(reservations) {
  if (!reservations) {
    return 0;
  }

  return reservations
    .map(
      (reservation) =>
        reservation.pre_adjusted_gross_including_packaged_item_in_cents
    )
    .reduce((a, b) => a + b, 0);
}

export function getTotalGrossInCents(reservations) {
  if (!reservations) {
    return 0;
  }

  return reservations
    .map((reservation) => reservation.gross_in_cents)
    .reduce((a, b) => a + b, 0);
}

export function sumOfGrossInCentsMinusCommission(reservations) {
  if (!reservations) {
    return 0;
  }

  return (
    sumBy(reservations, 'gross_in_cents') -
    sumBy(reservations, 'pre_adjusted_commission_in_cents')
  );
}

export function getTotalGrossIncludePackagedItem(reservations) {
  if (!reservations) {
    return 0;
  }

  return sumBy(reservations, 'gross_including_packaged_item_in_cents');
}

export function getPackagesCommission(reservations) {
  let commission = sumBy(reservations, 'pre_adjusted_commission_in_cents');
  reservations.forEach((reservation) => {
    const { sub_reservations_attributes: subReservations = [] } = reservation;
    commission += sumBy(subReservations, 'pre_adjusted_commission_in_cents');
  });

  return commission;
}

export function getDiscount(booking) {
  let discount = 0;

  if (booking.adjustments_attributes) {
    booking.adjustments_attributes.forEach((adjustment) => {
      const { gross_in_cents } = adjustment;
      if (gross_in_cents < 0) {
        discount += Math.abs(gross_in_cents);
      }
    });
  }

  if (booking.reservations_attributes) {
    booking.reservations_attributes.forEach((reservation) => {
      const diff =
        reservation.pre_adjusted_gross_including_packaged_item_in_cents -
        reservation.gross_including_packaged_item_in_cents;
      // There are 2 kinds of adjustments, one is discount and the other is surcharge
      if (diff > 0) {
        discount += diff;
      }
    });
  }

  return Math.abs(discount);
}

export const phoneNumber =
  window.ecom?.craftConfig?.phoneNumber?.plainText ||
  window.ecom?.craftConfig?.phoneNumber?.display;
export const phoneNumberLink =
  window.ecom?.craftConfig?.phoneNumber?.plainText2 ||
  window.ecom?.craftConfig?.phoneNumber?.tel;

export const parsePhoneNumberLink = (message) =>
  parse(
    message.replaceAll(
      '{{ phoneNumber }}',
      `<a className="text-reset" href=tel:${phoneNumberLink}>${phoneNumber}</a>`
    )
  );

export const emailAddress = window.ecom?.craftConfig?.emailAddress;

export const toSentence = (arrayOfStrings) =>
  arrayOfStrings.join(', ').replace(/,\s([^,]+)$/, ' and $1');

export const freezePage = () => {
  document.onkeydown = () => false;
  document.body.classList.add('no-click');
};

export const releasePage = () => {
  document.onkeydown = null;
  document.body.classList.remove('no-click');
};

// Get the current basename for React Route wrappers in 1SLK
export const getSeaLinkBasename = () => {
  const tenantName = window.location.pathname.split('/')[1];

  return tenantName || '/';
};

/**
 * Match /bookings/ferry, /bookings/:bookingId/ferry
 * returns A boolean value.
 */
export const isInFerryBookingPage = (pathname) =>
  !!pathname.match(
    /\/bookings\/\d+$|\/bookings\/\d+\/ferry|\/bookings\/ferry/i
  );

/**
 * Handles a redirection whether its in the navigation flow or not.
 */
export const historyOrReload = (history, path, reload) => {
  if (history && !reload) {
    history.push(path);
  } else {
    window.location = `${ecom.craftConfig.api_prefix}${path}`;
  }
};

export const getQuicktravelUrl = () => window.ecom.craftConfig.quicktravel_url;

/* Format accommodation property address */

export const formattedPropertyAddress = (property) =>
  `${property?.address?.address_line1}${
    property?.address?.address_line2 ? ' ' : ''
  }${property?.address?.address_line2},  ${property?.address?.post_code} ${
    property?.address?.city
  }, ${property?.address?.state}`;

// [null, null, null, true, true, true] -> [3, 4, 5]
// [null, true, null, true] -> [1, 3]
// https://stackoverflow.com/a/64670292
export const arrayKeyIndexes = (arr) => [...arr.keys()].filter((i) => arr[i]);

export const trackError = (error, message) => {
  if (window.Rollbar) {
    window.Rollbar.error(message || error.message, error);
  }
  console.error(error);
};

export const getFirstAvailableDeparture = (departures) => {
  const today = dateFormat(new Date(), FORMAT_QT_DATE);

  const availableDepartures = departures?.filter(
    (departure) =>
      departure.state === 'active' && departure.departs_on === today
  );

  return availableDepartures?.[0] ?? {};
};

// Check fetch status
export const checkStatus = (response) => {
  if (response.status >= 200 && response.status < 300) {
    return response.json();
  }

  if (response.status === 400) {
    return response.json().then((json) => {
      const error = new Error(json.message);
      error.response = response;
      throw error;
    });
  }
  const error = new Error(response.statusText);

  error.response = response;
  throw error;
};

export const applePaySupport = () => {
  if (window.location.protocol !== 'https:') {
    return false;
  }

  return window.ApplePaySession && window.ApplePaySession.canMakePayments();
};

export const removeDuplicatedVehicle = (vehicles) => {
  const vehiclesWithoutReg = vehicles.filter((item) => !item.registration);
  const vehiclesWithReg = vehicles.filter((item) => item.registration);
  return uniqBy(vehiclesWithReg, 'registration').concat(vehiclesWithoutReg);
};

export const fullFerrySelfServiceEnabled = () =>
  Boolean(ecom.craftConfig?.features?.fullFerrySelfService);

export const getAccountCreationSettings = () =>
  ecom.craftConfig?.features?.accountCreation || {};
